import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'

import { Flex, Container } from '@stackhouse/styled-elements'

import Text from '@stackhouse/dashboard-components/Text'
import Alert from '@stackhouse/dashboard-components/Alert'
import Button from '@stackhouse/dashboard-components/Button'
import Dropdown from '@stackhouse/dashboard-components/Dropdown'
import InputText from '@stackhouse/dashboard-components/Input/Text'
import InputRadio from '@stackhouse/dashboard-components/Input/Radio'
import BarcodeButton from '@stackhouse/dashboard-components/Barcode/Button'
import ResponsiveSwitch from '@stackhouse/dashboard-components/ResponsiveSwitch'

import { CheckboxBoolean as InputCheckbox } from '@stackhouse/dashboard-components/Input/Checkbox'

import Form from '../ResponsiveForm'
import CreateFilter from './createFilter'

import isMobile from '../../utils/is-mobile'


export default class SearchBox extends PureComponent {

    constructor(props) {
        super(props)
        this.formRef = React.createRef()
        this.dropdownRef = React.createRef()

        const { filters = [], openSections = [] } = props

        const { id, value: defaultFilter = null } = filters.find(filter => filter.default === 'Y') || {}
        const { query = '', barcodeSearch = false, fields: filter = {} } = JSON.parse(defaultFilter) || {}

        const filtersOptions = filters.map(filter => ({ ...filter, label: filter.title, value: filter.id }))
        this.state = {
            filters: filtersOptions,
            openSections,
            query,
            filter,
            barcodeSearch,
            currentFilter: id,
            advancedFilters: !!defaultFilter,
            shouldUpdateFilterValue: true,
        }
    }

    static propTypes = {
        fields: PropTypes.array,
        filters: PropTypes.array,
        openSections: PropTypes.array,
        onSearch: PropTypes.func,
        onChangeShowSort: PropTypes.func,
        saveFilter: PropTypes.func,
        deleteFilter: PropTypes.func,
        showSort: PropTypes.bool,
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.filters !== this.props.filters) {
            const { filters = [], openSections = [] } = this.props
            const filtersOptions = filters.map(filter => ({ ...filter, label: filter.title, value: filter.id }))

            if (this.state.shouldUpdateFilterValue) {
                const { id, value: defaultFilter = null } = filters.find(filter => filter.default === 'Y') || {}
                const { query = '', barcodeSearch = false, fields: filter = {} } = JSON.parse(defaultFilter) || {}

                return this.setState({ filters: filtersOptions, openSections, query, barcodeSearch, filter, currentFilter: id, advancedFilters: !!defaultFilter }, () => {
                    return this.formRef.current.reloadData(this.state.filter)
                })
            }
            else {
                return this.setState({ filters: filtersOptions, openSections, shouldUpdateFilterValue: true })
            }
        }

    }

    getFilter = () => {
        return { query: this.state.query, filter: this.formRef.current.getValue(), barcodeSearch: this.state.barcodeSearch }
    }

    onClickSearch = () => {
        const { onSearch } = this.props
        const values = this.formRef.current ? this.formRef.current.getValue() : {}
        return onSearch && this.setState({ openSections: null }, () => {
            return this.setState({ openSections: [] }, () => {
                return onSearch(this.state.query, values, this.state.barcodeSearch)
            })
        })
    }

    onKeyDown = (event) => {
        if (event.key === 'Enter') {
            return this.onClickSearch()
        }
    }

    onChangeShowSort = (showSort) => {

        if (this.props.onChangeShowSort) {
            this.props.onChangeShowSort(showSort)
        }

        if (this.dropdownRef.current) {
            this.dropdownRef.current.toggleVisible(false)
        }
    }

    onChangeShowAdvancedFilters = (advancedFilters) => {
        return this.setState({ advancedFilters }, () => {
            if (this.dropdownRef.current) {
                return this.dropdownRef.current.toggleVisible(false)
            }
        })
    }


    onChangeQuery = (query) => {
        return this.setState({ query, barcodeSearch: false })
    }

    onBarcodeScan = (query) => {
        return this.setState({ query, barcodeSearch: true })
    }

    onChangeFilter = (filter) => {
        return this.setState({ filter, currentFilter: null })
    }

    saveCurrentFilter = () => {
        return CreateFilter({
            onSave: (newFilter, close) => {
                const { saveFilter } = this.props
                const { filter, query, barcodeSearch } = this.state
                return this.setState({ shouldUpdateFilterValue: false }, () => {
                    return saveFilter(newFilter, filter, query, barcodeSearch, close)
                })
            }
        })
    }

    setFilter = (filterId) => {
        const { filters = [] } = this.props
        const { id, value = null } = filters.find(({ id }) => id === filterId) || {}
        const { query = '', barcodeSearch = false, fields: filter = {} } = JSON.parse(value) || {}
        return this.setState({ query, filter, barcodeSearch, currentFilter: id }, () => {
            return this.formRef.current.reloadData(filter)
        })
    }

    resetSearch = () => {
        return Alert.alert({
            text: 'Clear current filter?',
            closeOnClick: true,
            buttons: [
                {
                    title: 'Cancel',
                    outline: true
                },
                {
                    title: 'Confirm',
                    onClick: () => this.setState({ query: '', filter: {}, barcodeSearch: false, currentFilter: null }, () => {
                        return this.formRef.current.reloadData({})
                    })
                }
            ]
        }, { target: 'content' })
    }

    deleteFilter = (filter) => {
        const { deleteFilter } = this.props
        return deleteFilter && deleteFilter(filter)
    }

    renderRadioOption = filter => {
        return (
            <Flex alignCenter justifyBetween flex={1}>
                <Text flex={1}>{filter.title}</Text>
                <Button flex={0} icon={'delete'} iconSet={'md'} tooltip={'Delete'} onClick={this.deleteFilter.bind(null, filter)} />
            </Flex>
        )
    }

    get renderAdvancedFiltersDropdown() {
        const { showSort } = this.props
        const { advancedFilters, filters, currentFilter } = this.state
        return (
            <Container maxHeight={400} overflow={'auto'}>
                {isMobile ? (
                    <Flex column ph={10} pv={5} pb={0}>
                        <InputCheckbox
                            label={'Show sorting options'}
                            value={showSort}
                            validStates={[false, true]}
                            containerProps={{ padding: 0 }}
                            onChange={this.onChangeShowSort}
                        />
                    </Flex>
                ) : null}
                <Flex column ph={10} pv={5}>
                    <InputCheckbox
                        label={'Show advanced filters'}
                        value={advancedFilters}
                        validStates={[false, true]}
                        containerProps={{ padding: 0 }}
                        onChange={this.onChangeShowAdvancedFilters}
                    />
                    <InputRadio
                        value={currentFilter}
                        onChange={this.setFilter}
                        options={filters}
                        renderOption={this.renderRadioOption}
                    />
                    {advancedFilters ? <Button mt={10} title={'Save current filter...'} onClick={this.saveCurrentFilter} /> : null}
                </Flex>
            </Container>
        )
    }

    renderInputBarcodeButton = () => {
        if (!isMobile) return null
        return <BarcodeButton onScan={this.onBarcodeScan} />
    }

    render() {
        const { fields } = this.props
        const { query, filter, advancedFilters, currentFilter, openSections } = this.state

        const usingCustomFilter = currentFilter || Object.values(filter || {}).some(Boolean)

        return (
            <Flex column>
                <Flex row alignCenter justifyBetween ph={10} pb={[10, 5]} position={'sticky'} t={0} z={1000} backgroundColor={'white'}>
                    <InputText value={query} onChange={this.onChangeQuery} renderInnerRight={this.renderInputBarcodeButton} onKeyDown={this.onKeyDown} placeholder={'Search...'} height={35} hideLabelPadding containerProps={{ flex: 4 }} contentContainerProps={{ paddingVertical: 4 }} rounded />
                    <Container flex={0.15} />
                    <Flex column flex={[1, 0.5]} alignSelf={'stretch'}>
                        <ResponsiveSwitch>
                            <Button onClick={this.onClickSearch} reverse padding={'0 1em'} h={1} iconSet={'md'} icon={'search'} title={'Search'} />
                            <Button onClick={this.onClickSearch} reverse padding={'0 0.5em'} h={1} iconSet={'md'} icon={'search'} />
                        </ResponsiveSwitch>
                    </Flex>
                    <ResponsiveSwitch>
                        <Container flex={0.15} />
                        {null}
                    </ResponsiveSwitch>
                    <Flex column flex={0} alignSelf={'stretch'}>
                        <Button onClick={this.resetSearch} padding={'0 0.5em'} h={1} iconSet={'md'} tooltip={'Reset filter'} icon={'youtube_searched_for'} />
                    </Flex>
                    <ResponsiveSwitch>
                        <Container flex={0.15} />
                    </ResponsiveSwitch>
                    <ResponsiveSwitch>
                        <Dropdown iconSet={'md'} ref={this.dropdownRef} icon={'tune'} bottom right mt={10} p={0} buttonProps={{ link: !usingCustomFilter, outline: !!usingCustomFilter }} title={'Advanced Filters'}>{this.renderAdvancedFiltersDropdown}</Dropdown>
                        <Dropdown iconSet={'md'} ref={this.dropdownRef} icon={'tune'} bottom right mt={10} p={0} buttonProps={{ link: !usingCustomFilter, outline: !!usingCustomFilter, padding: 3 }}>{this.renderAdvancedFiltersDropdown}</Dropdown>
                    </ResponsiveSwitch>
                </Flex>
                <Flex column pb={[10, 0]} display={!advancedFilters ? 'none' : undefined}>
                    <Form ref={this.formRef} fields={fields} value={filter} onChange={this.onChangeFilter} openSections={openSections} />
                </Flex>
            </Flex>
        )
    }
}
